import React, { useEffect, useState } from 'react';
import { Button, Card, ListGroup } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { cameFromState } from '@/features/applications/faculty/backLink';
import { formatAnswerDay } from '@/shared/utils/deadline';
import applicationService from '@/shared/services/applicationService';
import { resumeDraftPath } from '@/features/applications/faculty/draftApplication';

const courseLabel = (app) =>
    app.course ? `${app.course.subject} ${app.course.courseNumber}` : 'Course';

/**
 * "Continue your support request": the person's saved drafts,
 * each opening back in the wizard where it was left (#519). Renders nothing
 * when there are none, or when they can't be loaded; the Applications page
 * lists them too.
 */
function DraftApplicationsCard() {
    const location = useLocation();
    const [drafts, setDrafts] = useState([]);

    useEffect(() => {
        let cancelled = false;
        Promise.resolve()
            .then(() => applicationService.getMyApplications())
            .then((apps) => {
                if (cancelled) return;
                setDrafts((apps || []).filter((a) => a.status === 'draft'));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    if (drafts.length === 0) return null;

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
                <h2 className="h5 mb-0">Continue your support request</h2>
            </Card.Header>
            <Card.Body>
                <ListGroup variant="flush">
                    {drafts.map((draft) => (
                        <ListGroup.Item
                            key={draft._id}
                            className="px-0 d-flex flex-wrap justify-content-between align-items-center gap-2"
                        >
                            <div>
                                <strong>{courseLabel(draft)}</strong>
                                {draft.course?.title && (
                                    <span className="text-muted ms-2">
                                        {draft.course.title}
                                    </span>
                                )}
                                {draft.courseConfirmation?.sectionNumber && (
                                    <span className="text-muted ms-2">
                                        Section{' '}
                                        {draft.courseConfirmation.sectionNumber}
                                    </span>
                                )}
                                <small className="d-block text-muted">
                                    Last saved{' '}
                                    {formatAnswerDay(draft.updatedAt)}
                                </small>
                                {draft.submitBlocker && (
                                    <small className="d-block text-danger">
                                        Can&apos;t be submitted as it stands:{' '}
                                        {draft.submitBlocker}
                                    </small>
                                )}
                            </div>
                            <Button
                                as={Link}
                                to={resumeDraftPath(draft._id)}
                                state={cameFromState(location)}
                                variant="primary"
                                size="sm"
                                aria-label={`Continue ${courseLabel(draft)} support request`}
                            >
                                Continue
                            </Button>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </Card.Body>
        </Card>
    );
}

export default DraftApplicationsCard;
